import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "motion/react";
import {
  Bell,
  ChefHat,
  CheckCircle2,
  ShoppingBag,
  Clock,
  X,
  RefreshCw,
  Wifi,
  WifiOff,
  Info,
} from "lucide-react";
import { toast } from '../components/ui/notify';
import { base_url } from "../api/index";
import { PosLoader } from "../components/pos/PosLoader";
import AccessDenied from "../components/AccessDenied";
import DontActiveAcount from "../components/DontActiveAcount";
import {
  getSystemNotifications,
  markSystemNotificationRead,
  markAllSystemNotificationsRead,
  removeSystemNotification,
  isSystemNotificationId,
} from "../api/pos/systemNotifications";
import {
  getDismissedIds,
  getReadIds,
  dismissNotifications,
  markNotificationsRead as persistRead,
} from "../api/pos/notificationState";
import { getKitchenDisplay, getKitchens, shortRef } from "../api/kitchen";
import { useI18n } from "../i18n/I18nContext";

const getHeaders = () => ({
  headers: {
    Authorization: `Bearer ${localStorage.getItem("token")}`,
    "Content-Type": "application/json",
    Accept: "application/json",
  },
});

type NotificationType = "kitchen" | "waiter" | "order" | "system";
type NotificationPriority = "low" | "medium" | "high";

interface AppNotification {
  id: string;
  type: NotificationType;
  priority: NotificationPriority;
  title: string;
  message: string;
  time: string;
  rawTime: Date;
  isRead: boolean;
  icon: any;
  actionable?: boolean;
  tableId?: number | null;
  tableName?: string | null;
}

const priorityConfig: Record<NotificationPriority, {
  color: string;
  textColor: string;
  bgLight: string;
  borderColor: string;
  gradientFrom: string;
}> = {
  high: {
    color: "bg-red-500",
    textColor: "text-red-500",
    bgLight: "bg-red-500/10",
    borderColor: "border-red-500/20",
    gradientFrom: "from-red-500/10",
  },
  medium: {
    color: "bg-amber-500",
    textColor: "text-amber-500",
    bgLight: "bg-amber-500/10",
    borderColor: "border-amber-500/20",
    gradientFrom: "from-amber-500/10",
  },
  low: {
    color: "bg-[var(--secondary)]",
    textColor: "text-[var(--secondary)]",
    bgLight: "bg-[var(--secondary)]/10",
    borderColor: "border-[var(--secondary)]/20",
    gradientFrom: "from-[var(--secondary)]/10",
  },
};

const typeLabels: Record<NotificationType, string> = {
  kitchen: "Mətbəx",
  waiter: "Garson çağrısı",
  order: "Sifariş",
  system: "Sistem",
};

const typeIconComponents: Record<NotificationType, any> = {
  kitchen: ChefHat,
  waiter: Bell,
  order: ShoppingBag,
  system: Info,
};

type TFn = (key: string, vars?: Record<string, string | number>) => string;

const formatRelativeTime = (date: Date, t: TFn): string => {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return t("time.now");
  if (diffMins < 60) return t("time.minAgo", { n: diffMins });
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return t("time.hourAgo", { n: diffHrs });
  return t("time.dayAgo", { n: Math.floor(diffHrs / 24) });
};

const POLL_INTERVAL = 15000;

/**
 * Cloud-a gedən sorğular üçün timeout.
 *
 * axios-un default `timeout` dəyəri 0-dır (SONSUZ) — internet kəsiləndə sorğu
 * OS-un TCP timeout-una qədər (onlarla saniyə) asılı qalırdı və bildirişlər
 * səhifəsi bu müddət ərzində boş görünürdü. Mətbəx datası pos_local-dan ani
 * gəlir, ona görə cloud-un ləngiməsi onu gözlətməməlidir.
 */
const CLOUD_TIMEOUT_MS = 5000;

const GenelAyarlar: React.FC = () => {
  const { t } = useI18n();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [filterType, setFilterType] = useState<NotificationType | "all">("all");
  const [showOnlyUnread, setShowOnlyUnread] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pollingError, setPollingError] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [activeUser, setActiveUser] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const notificationsRef = useRef<AppNotification[]>([]);

  const handleApiError = useCallback((error: any) => {
    if (error?.response?.status === 403) {
      if (error.response?.data?.message?.includes("active restaurant")) {
        setActiveUser(true);
      } else {
        setAccessDenied(true);
      }
    }
  }, []);

  const normalizeWaiterCalls = useCallback((raw: any[]): AppNotification[] => {
    return raw
      .filter((call) => call.status === "pending" || !call.status)
      .map((call) => ({
        id: `waiter-${call.id}`,
        type: "waiter" as NotificationType,
        priority: "high" as NotificationPriority,
        title: t("notif.waiterCall.title", {
          table:
            call.table?.name ||
            call.table_name ||
            `${t("common.table")} #${call.table_id}`,
        }),
        message: call.message || call.note || t("notif.waiterCall.requested"),
        time: formatRelativeTime(new Date(call.created_at || Date.now()), t),
        rawTime: new Date(call.created_at || Date.now()),
        isRead: call.status === "answered" || call.is_read || false,
        icon: Bell,
        actionable: true,
        tableId: call.table_id,
        tableName: call.table?.name || call.table_name || null,
      }));
  }, [t]);

  const normalizeKitchenNotifications = useCallback((tickets: any[]): AppNotification[] => {
    return tickets
      .filter((t) => t.overall_status === "ready")
      .map((ticket) => ({
        id: `kitchen-${ticket.id}`,
        type: "kitchen" as NotificationType,
        priority: "medium" as NotificationPriority,
        // pos_local sifariş nömrəsi vermir — id tam ULID-dir. Mətbəx lövhəsi
        // ilə eyni qısa istinad göstərilir ki, işçi iki ekranı tutuşdura bilsin.
        title: t("notif.kitchen.ready.title", { n: shortRef(ticket.order_id) }),
        message: t("notif.kitchen.ready.msg", {
          table: ticket.table || t("common.service"),
        }),
        time: formatRelativeTime(new Date(ticket.created_at || Date.now()), t),
        rawTime: new Date(ticket.created_at || Date.now()),
        isRead: false,
        icon: ChefHat,
        actionable: false,
        tableId: null,
        tableName: ticket.table || null,
      }));
  }, [t]);

  // POS "sistem" bildirişləri (catalogNotify → localStorage store). Garson/mətbəx
  // kimi cloud-dan gəlmir; lokal store-dan oxunub eyni siyahıya qoşulur.
  const normalizeSystemNotifications = useCallback((): AppNotification[] => {
    return getSystemNotifications().map((n) => {
      const rawTime = new Date(n.rawTimeIso);
      return {
        id: n.id,
        type: "system" as NotificationType,
        priority: "low" as NotificationPriority,
        title: n.title,
        message: n.message,
        time: formatRelativeTime(rawTime, t),
        rawTime,
        isRead: n.isRead,
        icon: typeIconComponents.system,
      };
    });
  }, [t]);

  const fetchNotifications = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      setPollingError(false);

      // Mətbəx sorğuları POS gateway ilə pos_local-a gedir (ani), qarşon
      // çağırışları isə YALNIZ cloud-dadır. Onları PARALEL başladırıq və
      // cloud-a timeout qoyuruq: internet kəsiləndə axios default-u sonsuz
      // gözləyir və bütün səhifə TCP timeout-una qədər donurdu.
      // NİYƏ xam axios DEYİL: əvvəl burada birbaşa `/kitchens/:id/display`
      // çağırılır və cavab olduğu kimi istifadə olunurdu. pos_local ticket-ində
      // `order_number` sahəsi yoxdur, ona görə başlıq interpolasiya olunmamış
      // "Sifariş hazır: #{n}" şəklində görünürdü. api/kitchen.ts-dəki
      // normalizeTicket bu sahələri (order_number, table, i18n adlar) qurur.
      const kitchenPromise = (async (): Promise<AppNotification[]> => {
        try {
          const kitchens = await getKitchens();
          if (kitchens.length === 0) return [];
          const display = await getKitchenDisplay(kitchens[0].id);
          return normalizeKitchenNotifications(display.tickets);
        } catch {
          return [];
        }
      })();

      const waiterPromise = axios
        .get(`${base_url}/waiter-calls`, { ...getHeaders(), timeout: CLOUD_TIMEOUT_MS })
        .then((res) => {
          const raw = res.data;
          const calls = Array.isArray(raw)
            ? raw
            : Array.isArray(raw?.data)
            ? raw.data
            : Array.isArray(raw?.waiter_calls)
            ? raw.waiter_calls
            : [];
          return normalizeWaiterCalls(calls);
        })
        .catch(() => [] as AppNotification[]);

      /**
       * Siyahını yaz — ən yeni öndə, oxunmuş vəziyyəti qoru.
       *
       * Mətbəx/garson bildirişləri CANLI vəziyyətdən törəyir: ticket "ready"
       * qaldıqca hər poll onu yenidən yaradır. Ona görə silinmiş id-lər
       * localStorage-dan süzülür — əks halda silinən bildiriş növbəti poll-da
       * geri qayıdırdı. Oxunmuş vəziyyət də oradan bərpa olunur, çünki səhifə
       * yenidən açılanda `prev` boş olur.
       */
      const apply = (list: AppNotification[]) => {
        const dismissed = getDismissedIds();
        const readStored = getReadIds();
        const sorted = list
          .filter((n) => !dismissed.has(n.id))
          .sort((a, b) => b.rawTime.getTime() - a.rawTime.getTime());
        setNotifications((prev) => {
          const readSet = new Set(prev.filter((n) => n.isRead).map((n) => n.id));
          return sorted.map((n) => ({
            ...n,
            isRead: n.isRead || readSet.has(n.id) || readStored.has(n.id),
          }));
        });
        setLastUpdated(new Date());
      };

      // 1) YERLİ mənbələr (mətbəx + POS sistem) — dərhal göstər, spinner-i bağla.
      const kitchenNotifs = await kitchenPromise;
      const localNotifs = [...kitchenNotifs, ...normalizeSystemNotifications()];
      apply(localNotifs);
      if (!silent) setLoading(false);

      // 2) Cloud (qarşon çağırışları) — gələndə siyahıya qarışdır. İnternet
      //    yoxdursa boş massiv qayıdır və ekran onsuz da dolu qalır.
      const waiterNotifs = await waiterPromise;
      if (waiterNotifs.length > 0) {
        apply([...waiterNotifs, ...localNotifs]);
      }
    } catch (error: any) {
      setPollingError(true);
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  }, [normalizeWaiterCalls, normalizeKitchenNotifications, normalizeSystemNotifications, handleApiError]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(() => fetchNotifications(true), POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  // POS sistem bildirişi gələndə (catalogNotify → store → `pos:system-notification`)
  // dərhal siyahıya inject et — 15s poll-u gözləmədən "Sistem" tabında görünsün.
  useEffect(() => {
    const syncSystem = () => {
      const dismissed = getDismissedIds();
      setNotifications((prev) => {
        const nonSystem = prev.filter((n) => n.type !== "system");
        const merged = [...nonSystem, ...normalizeSystemNotifications()].filter(
          (n) => !dismissed.has(n.id)
        );
        merged.sort((a, b) => b.rawTime.getTime() - a.rawTime.getTime());
        return merged;
      });
    };
    syncSystem();
    window.addEventListener("pos:system-notification", syncSystem);
    return () => window.removeEventListener("pos:system-notification", syncSystem);
  }, [normalizeSystemNotifications]);

  useEffect(() => {
    notificationsRef.current = notifications;
  }, [notifications]);

  const filteredNotifications = notifications.filter((notif) => {
    const matchesType = filterType === "all" || notif.type === filterType;
    const matchesRead = !showOnlyUnread || !notif.isRead;
    return matchesType && matchesRead;
  });

  const unreadCount = notifications.filter((n) => !n.isRead).length;
  const kitchenCount = notifications.filter((n) => n.type === "kitchen" && !n.isRead).length;
  const waiterCount = notifications.filter((n) => n.type === "waiter" && !n.isRead).length;
  const systemCount = notifications.filter((n) => n.type === "system" && !n.isRead).length;

  const markAsRead = (id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, isRead: true } : n))
    );
    // POS sistem bildirişləri lokal store-da saxlanır — read vəziyyəti orada da qalsın.
    if (isSystemNotificationId(id)) markSystemNotificationRead(id);
    else persistRead([id]); // törədilən bildiriş: poll onu geri "oxunmamış" etməsin
  };

  const markAllAsRead = () => {
    persistRead(notificationsRef.current.map((n) => n.id));
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
    markAllSystemNotificationsRead();
    toast.success(t("notif.allRead"));
  };

  const deleteNotification = (id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
    if (isSystemNotificationId(id)) removeSystemNotification(id);
    // Törədilən bildiriş üçün silmək = "bir daha göstərmə". Ticket hələ də
    // "ready" olduğuna görə növbəti poll onu yenidən yaradardı.
    else dismissNotifications([id]);
    toast.success(t("notif.deleted"));
  };

  const handleAction = async (notification: AppNotification) => {
    markAsRead(notification.id);
    if (notification.type === "waiter" && notification.tableId) {
      try {
        // Garson çağrısını "answered" olarak işaretle
        await axios.patch(
          `${base_url}/waiter-calls/${notification.id.replace("waiter-", "")}/answer`,
          {},
          getHeaders()
        ).catch(() => null); // API yoksa sessizce geç
        toast.success(t("notif.waiterAnswered", { table: notification.tableName || notification.tableId || "" }));
      } catch {
        toast.info(t("notif.processed", { title: notification.title }));
      }
    } else {
      toast.success(t("notif.processed", { title: notification.title }));
    }
  };

  if (accessDenied) return <AccessDenied onClose={() => setAccessDenied(false)} />;
  if (activeUser) return <DontActiveAcount sil={setActiveUser} />;

  return (
    <div
      className="h-[calc(100vh-5rem)] flex flex-col overflow-hidden"
      style={{ backgroundColor: "var(--bg-body)" }}
    >
      {/* Toast: App-dəki tək qlobal ToastContainer istifadə olunur (react-toastify
          çoxlu konteyner konfliktinə qarşı — "Cannot set toggle" xətası). */}
      <Helmet>
        <title>{t("notif.title")} | Smartcafe</title>
        <meta name="description" content="Restoran bildirimleri | Garson çağrıları ve mutfak bildirimleri" />
      </Helmet>

      {/* Header */}
      <div
        className="border-b border-black/5 dark:border-white/10 px-6 py-5 shrink-0"
        style={{ backgroundColor: "var(--bg-card)" }}
      >
        <div className="flex items-center justify-between mb-5">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <h1 className="text-2xl font-black text-gray-900 dark:text-white">
                {t("notif.title")}
              </h1>
              {unreadCount > 0 && (
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="px-2.5 py-0.5 bg-red-500 text-white rounded-full text-sm font-black"
                >
                  {unreadCount}
                </motion.span>
              )}
              {pollingError && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-red-500/10 border border-red-500/20">
                  <WifiOff className="w-3.5 h-3.5 text-red-500" />
                  <span className="text-xs font-bold text-red-500">{t("notif.connError")}</span>
                </div>
              )}
              {!pollingError && lastUpdated && (
                <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-green-500/10 border border-green-500/20">
                  <Wifi className="w-3.5 h-3.5 text-green-500" />
                  <span className="text-xs font-semibold text-green-600 dark:text-green-400">{t("notif.live")}</span>
                </div>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {lastUpdated
                ? `${t("notif.lastUpdate")}: ${formatRelativeTime(lastUpdated, t)}`
                : `${t("common.loading")}...`
              }
            </p>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchNotifications()}
              className="p-2.5 rounded-xl bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 transition-colors text-gray-500 dark:text-gray-400"
              title={t("common.refresh")}
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              onClick={markAllAsRead}
              disabled={unreadCount === 0}
              className="px-4 py-2 bg-[var(--primary)] text-white rounded-xl hover:bg-orange-600 transition-all flex items-center gap-2 font-bold text-sm disabled:opacity-40 disabled:cursor-not-allowed shadow-md shadow-orange-500/20"
            >
              <CheckCircle2 className="w-4 h-4" />
              {t("notif.markAllRead")}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: t("notif.type.waiter"), count: waiterCount, icon: Bell, color: "text-red-500", bg: "bg-red-500/10", border: "border-red-500/20" },
            { label: t("notif.type.kitchen"), count: kitchenCount, icon: ChefHat, color: "text-[var(--primary)]", bg: "bg-[var(--primary)]/10", border: "border-[var(--primary)]/20" },
            { label: t("notif.type.system"), count: systemCount, icon: Info, color: "text-[var(--secondary)]", bg: "bg-[var(--secondary)]/10", border: "border-[var(--secondary)]/20" },
          ].map(({ label, count, icon: Icon, color, bg, border }) => (
            <div
              key={label}
              className={`rounded-xl p-3 border ${bg} ${border} flex items-center gap-3`}
            >
              <div className={`w-8 h-8 rounded-lg ${bg} flex items-center justify-center`}>
                <Icon className={`w-4 h-4 ${color}`} />
              </div>
              <div>
                <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
                <p className={`text-xl font-black ${color}`}>{count}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Filters */}
      <div
        className="border-b border-black/5 dark:border-white/10 px-6 py-3 shrink-0"
        style={{ backgroundColor: "var(--bg-card)" }}
      >
        <div className="flex items-center gap-3">
          <div
            className="flex gap-1 rounded-xl p-1 border border-black/5 dark:border-white/10"
            style={{ backgroundColor: "var(--bg-elevated)" }}
          >
            {(["all", "waiter", "kitchen", "system"] as const).map((type) => (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-colors ${
                  filterType === type
                    ? "bg-[var(--primary)] text-white shadow-sm"
                    : "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
                }`}
              >
                {type === "all" ? t("notif.tab.all") : t(`notif.type.${type}`)}
              </button>
            ))}
          </div>

          <div className="h-6 w-px bg-black/10 dark:bg-white/10" />

          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showOnlyUnread}
              onChange={(e) => setShowOnlyUnread(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 dark:border-gray-600 text-[var(--primary)] focus:ring-[var(--primary)]"
            />
            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
              {t("notif.onlyUnread")}
            </span>
          </label>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto pos-scroll p-6">
        {loading ? (
          <div className="h-full flex items-center justify-center">
            <PosLoader size="lg" label={t("notif.loading")} />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="h-full flex flex-col items-center justify-center gap-4 text-gray-400 dark:text-gray-600"
          >
            <div className="relative">
              <div className="absolute inset-0 rounded-[2rem] bg-[var(--primary)]/10 blur-2xl" />
              <div className="relative w-24 h-24 rounded-[2rem] bg-gradient-to-br from-[var(--primary)]/10 to-amber-500/10 border border-[var(--primary)]/20 flex items-center justify-center">
                <Bell className="w-10 h-10 text-[var(--primary)]/50" />
              </div>
            </div>
            <div className="text-center">
              <p className="text-lg font-black text-gray-700 dark:text-gray-300">
                {t("notif.empty")}
              </p>
              <p className="text-sm mt-1">
                {showOnlyUnread
                  ? t("notif.allReadEmpty")
                  : filterType !== "all"
                  ? t("notif.empty.category")
                  : t("notif.empty.new")}
              </p>
            </div>
          </motion.div>
        ) : (
          <div className="max-w-3xl mx-auto space-y-3">
            <AnimatePresence mode="popLayout">
              {filteredNotifications.map((notification) => {
                const Icon = notification.icon || typeIconComponents[notification.type];
                const priorityStyle = priorityConfig[notification.priority];

                return (
                  <motion.div
                    key={notification.id}
                    layout
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, x: -100 }}
                    transition={{ type: "spring", damping: 20, stiffness: 200 }}
                    className={`pos-card rounded-2xl p-5 border transition-all ${
                      notification.isRead
                        ? "opacity-60 border-black/5 dark:border-white/5"
                        : `${priorityStyle.borderColor} shadow-md shadow-black/5 dark:shadow-black/25 hover:shadow-lg`
                    }`}
                    style={{ backgroundColor: "var(--bg-card)" }}
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div
                        className={`w-11 h-11 rounded-xl flex items-center justify-center shrink-0 ${
                          notification.isRead
                            ? "bg-gray-100 dark:bg-white/5"
                            : priorityStyle.bgLight
                        }`}
                      >
                        <Icon
                          className={`w-5 h-5 ${
                            notification.isRead
                              ? "text-gray-400 dark:text-gray-600"
                              : priorityStyle.textColor
                          }`}
                        />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3 mb-1.5">
                          <div className="flex-1">
                            <h3 className="font-bold text-gray-900 dark:text-white leading-snug">
                              {notification.title}
                            </h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed mt-0.5">
                              {notification.message}
                            </p>
                          </div>
                          {!notification.isRead && (
                            <div
                              className={`w-2.5 h-2.5 rounded-full ${priorityStyle.color} shrink-0 mt-1.5`}
                            />
                          )}
                        </div>

                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5 text-xs text-gray-400 dark:text-gray-500">
                              <Clock className="w-3.5 h-3.5" />
                              <span>{notification.time}</span>
                            </div>
                            <span
                              className={`px-2 py-0.5 rounded-md text-xs font-bold ${
                                notification.type === "waiter"
                                  ? "bg-red-500/10 text-red-600 dark:text-red-400"
                                  : notification.type === "kitchen"
                                  ? "bg-[var(--primary)]/10 text-[var(--primary)]"
                                  : "bg-[var(--secondary)]/10 text-[var(--secondary)]"
                              }`}
                            >
                              {t(`notif.type.${notification.type}`)}
                            </span>
                          </div>

                          <div className="flex items-center gap-2">
                            {notification.actionable && !notification.isRead && (
                              <button
                                onClick={() => handleAction(notification)}
                                className="px-3 py-1.5 bg-[var(--primary)] text-white rounded-lg hover:bg-orange-600 transition-all text-xs font-bold shadow-sm"
                              >
                                {t("notif.reply")}
                              </button>
                            )}
                            {!notification.isRead && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-white/10 transition-all text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                title={t("notif.markRead")}
                              >
                                <CheckCircle2 className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-all text-gray-400 hover:text-red-500"
                              title={t("common.delete")}
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenelAyarlar;
